/**
 * The user's cast (api/routes/characters.py), shared by the Characters page, the
 * character picker on the create page and the series creator: one query, so a
 * character made in one place shows up in the others at once.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';

export const CHARACTERS_KEY = ['characters'];
export const MAX_PER_VIDEO = 3;

export default function useCharacters({ enabled = true } = {}) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: CHARACTERS_KEY, queryFn: api.listCharacters, enabled, staleTime: 60 * 1000 });
  const characters = query.data?.characters || [];
  return {
    characters,
    byId: Object.fromEntries(characters.map((character) => [character.id, character])),
    // null: no cap (an admin's list says so); 12 until the list is in
    maxCharacters: query.data && 'max_characters' in query.data ? query.data.max_characters : 12,
    // A series holds more than a video: each episode follows one of them in turn. null: no cap
    // (2026-10-10, the user: as many as they want), read as Infinity; 3 until the list is in
    maxPerSeries: query.data && 'max_per_series' in query.data ? (query.data.max_per_series ?? Infinity) : MAX_PER_VIDEO,
    // Styles a photo of a real person is never drawn in (api/services/characters.py)
    photographicStyles: query.data?.photographic_styles || [],
    loading: query.isLoading,
    error: query.isError,
    // After a change, or with the character the API answered (it has a fresh portrait link)
    remember: (character) => queryClient.setQueryData(CHARACTERS_KEY, (current) => {
      const list = current?.characters || [];
      const others = list.filter((item) => item.id !== character.id);
      return { ...(current || {}), characters: [...others, character].sort((a, b) => String(a.created_at).localeCompare(String(b.created_at))) };
    }),
    forget: (characterId) => queryClient.setQueryData(CHARACTERS_KEY, (current) => ({
      ...(current || {}), characters: (current?.characters || []).filter((item) => item.id !== characterId),
    })),
  };
}

/** The API's refusal as a copy key under characters.errors. */
export function characterError(error) {
  const detail = error?.response?.data?.detail;
  const known = ['character_incomplete', 'too_many_characters', 'name_taken', 'too_many_portraits', 'portrait_failed', 'character_not_found',
    'photo_of_a_child', 'photo_consent_needed', 'photo_needs_illustrated_style', 'picture_unreadable', 'picture_too_large',
    'too_many_looks', 'look_failed'];
  return known.includes(detail) ? detail : 'generic';
}
